import { CustomCalendar } from "components/CustomCalendar/CustomCalendar";
import { Select } from "components/ui/Select/Select";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";
import { sexOptions } from "utils/patientFormOptions";

import styles from "./ReportsFilters.module.css";

export const formatDateValue = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const GMS_OPTIONS = [
  { label: "Medical Card", value: "medical-card" },
  { label: "Doctor Visit Card", value: "doctor-visit-card" },
  { label: "Private", value: "private" },
];

// Gender filter, shared by the CDM finder / recall / register and the patient
// reports: spec allows Male / Female / Other / All (the shared sexOptions only
// carry Male / Female). "Other" means neither male nor female — see
// ReportGenderFilter in api-practice, which also matches case-insensitively so
// a patient stored as "MALE" is not silently dropped (KIN-1213).
export const REPORT_GENDER_OPTIONS = [
  { value: "", label: "Any gender" },
  ...sexOptions.map((sex) => ({ value: sex, label: sex })),
  { value: "Other", label: "Other" },
];

export const Field = ({ label, children }) => (
  <div className={styles.filterField}>
    <label className={styles.filterLabel}>{label}</label>
    {children}
  </div>
);

export const TextInput = ({ value, onChange, placeholder, className }) => (
  <input
    type="text"
    className={className || styles.filterInput}
    value={value || ""}
    onChange={(e) => onChange(e.target.value || "")}
    placeholder={placeholder}
  />
);

export const NumberInput = ({ value, onChange }) => (
  <input
    type="text"
    inputMode="numeric"
    className={styles.filterInput}
    value={value || ""}
    onInput={(e) => (e.target.value = e.target.value.replace(/[^0-9]/g, ""))}
    onChange={(e) => onChange(e.target.value || "")}
    placeholder="Age"
  />
);

export const DateField = ({ label, value, onChange, placeholder }) => (
  <Field label={label}>
    <CustomCalendar
      value={value || null}
      onChange={(date) => onChange(formatDateValue(date))}
      placeholder={placeholder || "Select date"}
    />
  </Field>
);

// `placeholder` is only passed by callers whose options already carry their own
// empty entry ("Any gender"): they pass null to suppress Select's built-in
// "Select", so the list does not end up with two value="" options — which makes
// choosing the empty one display "Select" and look stuck.
export const SelectField = ({ label, value, onChange, options, placeholder }) => (
  <Field label={label}>
    <div className={styles.filterSelect}>
      <Select
        options={options}
        value={value || ""}
        onChange={(val) => onChange(val || "")}
        {...(placeholder === undefined ? {} : { placeholder })}
        fullWidth
      />
    </div>
  </Field>
);

// The finder lays selects next to 48px text inputs / MultiSelects, so it uses
// the shared Select at its native 48px height (via .finderSelect) instead of
// the report filters' .filterSelect, which forces 44px and a different text
// size/colour.
export const FinderSelectField = ({ label, value, onChange, options }) => (
  <Field label={label}>
    <div className={styles.finderSelect}>
      <Select
        options={options}
        value={value || ""}
        onChange={(val) => onChange(val || "")}
        // Options already include their own empty-value entry ("All programmes"
        // / "Any …"); suppress Select's built-in placeholder so there aren't two
        // value="" options (which would make selecting the empty one show
        // "Select" and appear stuck).
        placeholder={null}
        fullWidth
      />
    </div>
  </Field>
);

// Grouped variant of FinderSelectField — renders the shared Select with
// <optgroup>s (spec p.18 finder dropdown). A placeholder leads the list so an
// unselected picker reads "Select a finder".
export const GroupedFinderSelectField = ({
  label,
  value,
  onChange,
  groups,
  placeholder,
}) => (
  <Field label={label}>
    <div className={styles.finderSelect}>
      <Select
        groups={groups}
        value={value || ""}
        onChange={(val) => onChange(val || "")}
        placeholder={placeholder}
        fullWidth
      />
    </div>
  </Field>
);

export const GmsField = ({ value, onChange }) => (
  <SelectField
    label="GMS Status"
    options={GMS_OPTIONS}
    value={value}
    onChange={onChange}
  />
);

export const GenderField = ({ value, onChange }) => (
  <SelectField
    label="Gender"
    options={REPORT_GENDER_OPTIONS}
    value={value}
    onChange={onChange}
    placeholder={null}
  />
);

export const PatientFields = ({ filters, onFilterChange, withGms = true }) => (
  <>
    <Field label="First Name">
      <TextInput
        value={filters.patientFirstname}
        onChange={(v) => onFilterChange("patientFirstname", v)}
        placeholder="First name"
      />
    </Field>
    <GenderField
      value={filters.gender}
      onChange={(v) => onFilterChange("gender", v)}
    />
    <Field label="Age">
      <NumberInput
        value={filters.patientAge}
        onChange={(v) => onFilterChange("patientAge", v)}
      />
    </Field>
    {withGms && (
      <GmsField
        value={filters.patientGmsStatus}
        onChange={(v) => onFilterChange("patientGmsStatus", v)}
      />
    )}
  </>
);

export const StaffMemberDropdown = ({ label = "Staff Member", value, onChange }) => {
  const { data: staffMembers = [], isLoading } = useStaffMembers();

  const options = [
    { value: "", label: "All staff" },
    ...staffMembers.map((staff) => ({
      value: String(staff.id),
      label: `${staff.firstName || ""} ${staff.lastName || ""}`.trim() || `Staff ${staff.id}`,
    })),
  ];

  return (
    <SelectField
      label={label}
      options={options}
      value={value || ""}
      onChange={onChange}
      placeholder={isLoading ? "Loading..." : null}
    />
  );
};
